// #genai Phase 2 shared "Add Payment Method" button + payment-type menu styles.
// Pixel-matched to Figma: button (269:29310 / 269:29328), desktop dropdown (630:31658 / 630:31801),
// mobile bottom-sheet drawer (305:26485 / 305:26528). Used by both the billing empty state and the
// saved-payment-methods list so the affordance stays identical.
import { makeStyles } from 'lib/components';

export default makeStyles(() => ({
  // Anchor wrapper so the desktop popper aligns to the button. Hugs the button on desktop;
  // full width on mobile.
  root: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '@media (max-width: 639px)': {
      display: 'flex',
      width: '100%',
    },
  },

  // "Add Payment Method" — bg #007CAD, white 16px medium, tracking 0.32px, h-44, radius 4.
  // Desktop: pl-12 pr-16 with a 8px gap before the chevron. Mobile: full width, centered.
  addButton: {
    height: '44px',
    // #genai Match the 232px dropdown menu width so the button and the two option rows align (Figma).
    width: '232px',
    // #genai Label left, chevron right (space-between) instead of centered.
    justifyContent: 'space-between',
    padding: '10px 16px 10px 12px',
    borderRadius: '4px',
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.32px',
    textTransform: 'none',
    backgroundColor: '#007CAD',
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    '& .MuiButton-endIcon': {
      marginLeft: '8px',
      marginRight: 0,
    },
    '& .MuiButton-endIcon > *': {
      fontSize: '24px',
    },
    '&:hover': {
      backgroundColor: '#005A8D',
    },
    '@media (max-width: 639px)': {
      width: '100%',
      padding: '10px 16px',
      justifyContent: 'center',
    },
  },

  // Pressed/open state (Figma 630:31668): button darkens to ocean-700 while the menu is open.
  addButtonOpen: {
    backgroundColor: '#005A8D',
  },

  // ── Desktop dropdown menu (Figma 630:31801) ──
  popper: {
    zIndex: 1300,
  },

  // 232px, radius 4, ev-elevation--z06 drop shadow, clipped corners.
  menu: {
    marginTop: '4px',
    width: '232px',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    boxShadow:
      '0px 6px 5px 0px rgba(0,0,0,0.14), 0px 1px 9px 0px rgba(0,0,0,0.12), 0px 3px 2.5px 0px rgba(0,0,0,0.2)',
  },

  // Dropdown row — reset native button, white bg, space title and icon apart.
  // Desktop px-8 py-6 (Figma 630:31802). Mobile px-8 py-16 / h-62 (Figma 305:26568).
  optionItem: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '6px 8px',
    backgroundColor: '#FFFFFF',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'Noto Sans',
    '&:hover': {
      backgroundColor: '#F0F2F7',
    },
    '@media (max-width: 639px)': {
      minHeight: '62px',
      padding: '16px 8px',
    },
  },

  // Divider under the first option. Desktop #C8CDD7, mobile #E6EAF1.
  optionItemDivider: {
    borderBottom: '1px solid #C8CDD7',
    '@media (max-width: 639px)': {
      borderBottom: '1px solid #E6EAF1',
    },
  },

  optionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  optionTitle: {
    fontFamily: 'Noto Sans',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#1B202C',
  },

  optionSubtitle: {
    fontFamily: 'Noto Sans',
    fontSize: '14px',
    fontWeight: 400,
    fontStyle: 'italic', // #genai Figma updated "Preferred" subtitle to italic
    lineHeight: '18px',
    color: '#61687D',
  },

  optionIcon: {
    width: '24px',
    height: '24px',
    flexShrink: 0,
    color: '#1B202C',
  },

  // ── Mobile bottom-sheet drawer (Figma 305:26528) ──
  // White surface, 1px #E5E5E5 border, rounded top corners, px-12.
  drawerPaper: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E5E5',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    padding: '0 12px 12px',
  },

  // Drag-handle row — pt-16, centered.
  drawerHandleWrap: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
  },

  // 120x8 muted (#F5F5F5) pill.
  drawerHandle: {
    width: '120px',
    height: '8px',
    borderRadius: '9999px',
    backgroundColor: '#F5F5F5',
  },

  drawerOptions: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '8px',
  },
}));
